import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiSparkleFill } from "react-icons/pi";
import { EducaeetCTA } from "../../educaeet";
import { HokCta } from "../../hok";

export default function CtaSection({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const secondaryColor = content.colorPalette?.secondary || "#4F46E5";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    if (content.variant === "hok") {
        return <HokCta content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (content.variant === 'educaeet') {
        return <EducaeetCTA {...{ content, onContentChange, isSelected }} />;
    }

    return (
        <div
            className={`relative overflow-hidden py-24 px-8 text-center ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            {/* Background Gradient */}
            <div
                className="absolute inset-0 -z-10"
                style={{ background: `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}10)` }}
            />

            {/* Decorative shapes */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl opacity-50 -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl opacity-50 translate-x-1/2 translate-y-1/2"></div>

            <div className="mx-auto max-w-4xl relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-100 shadow-sm text-sm font-medium text-gray-600 mb-8 animate-[fade-in_1s]">
                    <PiSparkleFill className="text-yellow-400" />
                    <EditableText
                        value={content.subtitle || "Limited Time Offer"}
                        onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                        as="span"
                        placeholder="Subtitle"
                    />
                </div>

                <h2 className="mb-8 text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight">
                    <EditableText
                        value={content.title || "Ready to get started?"}
                        onChange={(val) => onContentChange?.({ ...content, title: val })}
                        as="span"
                        placeholder="CTA Title"
                    />
                </h2>

                <p className="mb-10 text-xl text-gray-600 max-w-2xl mx-auto">
                    <EditableText
                        value={content.description || "Join thousands of developers building the future today."}
                        onChange={(val) => onContentChange?.({ ...content, description: val })}
                        as="span"
                        placeholder="Description text"
                    />
                </p>

                <a
                    href={onContentChange ? undefined : (content.primaryCtaLink || content.ctaLink || content.buttonLink || "#")}
                    target={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                    rel={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                    onClick={(e) => onContentChange && e.preventDefault()}
                    className="inline-flex items-center justify-center rounded-full px-10 py-4 text-lg font-bold text-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl cursor-pointer"
                    style={{
                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                        boxShadow: `0 10px 25px -5px ${primaryColor}50`
                    }}
                >
                    <EditableText
                        value={content.buttonText || content.primaryCta || "Get Started Now"}
                        onChange={(val) => onContentChange?.({ ...content, buttonText: val })}
                        as="span"
                        placeholder="Button Text"
                    />
                </a>

                <p className="mt-6 text-sm text-gray-500">
                    No credit card required · 14-day free trial
                </p>
            </div>
        </div>
    );
}
